/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import adminApi from '../../services/adminApi';
import AdminLayout from '../../components/AdminLayout';
import './TrainingJobDetail.css';

const TrainingJobDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  /**
   * Load job details (wrapped in useCallback to avoid stale closures)
   */
  const loadJob = useCallback(async () => {
    try {
      setError('');
      // adminApi.getTrainingJob already returns response.data
      // So 'data' here is directly: { success: true, job: {...} }
      const data = await adminApi.getTrainingJob(jobId);
      const jobData = data.job || data;  // Extract job from response
      setJob(jobData);
      console.log('[TrainingJobDetail] Job loaded:', {
        jobId: jobData.jobId,
        status: jobData.status,
        progress: jobData.progress,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      console.error('Load job error:', err);
      setError(err.response?.data?.message || 'Failed to load job details');
    } finally {
      setLoading(false);
    }
  }, [jobId]); // Only recreate if jobId changes

  /**
   * Initial load
   */
  useEffect(() => {
    loadJob();
  }, [loadJob]);

  /**
   * Auto-refresh for active jobs
   */
  useEffect(() => {
    if (!autoRefresh || !job) return;

    // Only refresh if job is in progress
    const activeStatuses = ['queued', 'preparing_data', 'uploading_dataset', 'submitting_training', 'training'];
    if (!activeStatuses.includes(job.status)) {
      console.log('[TrainingJobDetail] Auto-refresh stopped - job not active:', job.status);
      return;
    }

    console.log('[TrainingJobDetail] Auto-refresh enabled - polling every 10s');
    const interval = setInterval(() => {
      console.log('[TrainingJobDetail] Auto-refresh tick - fetching latest job data');
      loadJob();
    }, 10000); // Refresh every 10 seconds

    return () => {
      console.log('[TrainingJobDetail] Auto-refresh cleanup');
      clearInterval(interval);
    };
  }, [autoRefresh, job?.status, loadJob]); // Added loadJob to dependencies

  /**
   * Handle retry
   */
  const handleRetry = async () => {
    if (!window.confirm('Retry this training job?')) return;

    try {
      await adminApi.retryTrainingJob(jobId);
      await loadJob();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to retry job');
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = async () => {
    if (!window.confirm('Cancel this training job? This cannot be undone.')) return;

    try {
      await adminApi.cancelTrainingJob(jobId);
      await loadJob();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel job');
    }
  };

  /**
   * Get status class
   */
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'status-success';
      case 'training':
      case 'uploading_dataset':
      case 'submitting_training':
        return 'status-running';
      case 'failed':
        return 'status-failed';
      case 'queued':
      case 'preparing_data':
        return 'status-pending';
      default:
        return 'status-unknown';
    }
  };

  /**
   * Format date
   */
  const formatDate = (dateString) => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  /**
   * Format duration
   */
  const formatDuration = () => {
    if (!job.startedAt) return '--';

    const start = new Date(job.startedAt);
    const end = job.completedAt ? new Date(job.completedAt) : new Date();
    const durationMs = end - start;

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (loading) {
    return (
      <div className="job-detail-loading">
        <div className="spinner-large"></div>
        <p>Loading job details...</p>
      </div>
    );
  }

  if (error && !job) {
    return (
      <div className="job-detail-error">
        <div className="error-icon">⚠️</div>
        <h2>Error Loading Job</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/admin/training/jobs')}>Back to Jobs</button>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="job-detail-error">
        <div className="error-icon">⚠️</div>
        <h2>Job Not Found</h2>
        <p>The requested job could not be found or loaded.</p>
        <button onClick={() => navigate('/admin/training/jobs')}>Back to Jobs</button>
      </div>
    );
  }

  return (
    <div className="job-detail">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate('/admin/training/jobs')}>
            ← Back to Jobs
          </button>
          <div>
            <h1>Training Job Details</h1>
            <p className="job-id">Job ID: <code>{job.jobId}</code></p>
          </div>
        </div>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <button className="refresh-button" onClick={loadJob}>
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Status Card */}
      <div className="detail-card status-card">
        <h2>Status & Progress</h2>
        <div className="status-info">
          <div className="status-item">
            <span className="label">Status:</span>
            <span className={`status-badge ${getStatusClass(job.status)}`}>
              {job.status}
            </span>
          </div>
          <div className="status-item">
            <span className="label">Current Phase:</span>
            <span className="phase-text">{job.currentPhase || '--'}</span>
          </div>
          <div className="status-item">
            <span className="label">Progress:</span>
            <div className="progress-container">
              <div className="progress-bar-large">
                <div
                  className="progress-fill"
                  style={{ width: `${job.progress || 0}%` }}
                ></div>
              </div>
              <span className="progress-percent">{job.progress || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Kaggle Integration Card */}
      <div className="detail-card kaggle-card">
        <h2>Kaggle Integration</h2>
        {job.kaggle?.datasetSlug || job.kaggle?.datasetUrl || job.kaggle?.notebookId ? (
          <div className="kaggle-info">
            {job.kaggle.datasetSlug && (
              <div className="kaggle-item">
                <span className="label">Dataset:</span>
                <div className="kaggle-link-group">
                  <code className="kaggle-slug">{job.kaggle.datasetSlug}</code>
                  {job.kaggle.datasetUrl && (
                    <a
                      href={job.kaggle.datasetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="kaggle-link-btn"
                    >
                      View Dataset →
                    </a>
                  )}
                </div>
              </div>
            )}
            {job.kaggle.notebookId && (
              <div className="kaggle-item">
                <span className="label">Notebook:</span>
                <div className="kaggle-link-group">
                  <code className="kaggle-slug">{job.kaggle.notebookId}</code>
                  <a
                    href={`https://www.kaggle.com/code/${job.kaggle.notebookId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="kaggle-link-btn"
                  >
                    View Notebook →
                  </a>
                </div>
              </div>
            )}
            {job.kaggle.logUrl && (
              <div className="kaggle-item">
                <span className="label">Logs:</span>
                <a
                  href={job.kaggle.logUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kaggle-link-btn"
                >
                  View Kaggle Logs →
                </a>
              </div>
            )}
          </div>
        ) : (
          <p className="no-data">No Kaggle integration data available yet</p>
        )}
      </div>

      {/* Configuration Card */}
      <div className="detail-card config-card">
        <h2>Configuration</h2>
        <div className="config-grid">
          <div className="config-item">
            <span className="label">Domain:</span>
            <span className="value">{job.domain}</span>
          </div>
          {job.subDomain && (
            <div className="config-item">
              <span className="label">Sub-Domain:</span>
              <span className="value">{job.subDomain}</span>
            </div>
          )}
          <div className="config-item">
            <span className="label">Base Model:</span>
            <span className="value">{job.config?.baseModel || '--'}</span>
          </div>
          <div className="config-item">
            <span className="label">Training Records:</span>
            <span className="value">{job.config?.trainingData?.recordCount || '--'}</span>
          </div>
          <div className="config-item">
            <span className="label">Epochs:</span>
            <span className="value">{job.config?.hyperparameters?.epochs || '--'}</span>
          </div>
          <div className="config-item">
            <span className="label">Learning Rate:</span>
            <span className="value">{job.config?.hyperparameters?.learningRate || '--'}</span>
          </div>
          <div className="config-item">
            <span className="label">Batch Size:</span>
            <span className="value">{job.config?.hyperparameters?.batchSize || '--'}</span>
          </div>
          <div className="config-item">
            <span className="label">LoRA R:</span>
            <span className="value">{job.config?.hyperparameters?.loraR || '--'}</span>
          </div>
        </div>
      </div>

      {/* Timeline Card */}
      <div className="detail-card timeline-card">
        <h2>Timeline</h2>
        <div className="timeline-grid">
          <div className="timeline-item">
            <span className="label">Created:</span>
            <span className="value">{formatDate(job.createdAt)}</span>
          </div>
          <div className="timeline-item">
            <span className="label">Started:</span>
            <span className="value">{formatDate(job.startedAt)}</span>
          </div>
          <div className="timeline-item">
            <span className="label">Completed:</span>
            <span className="value">{formatDate(job.completedAt)}</span>
          </div>
          <div className="timeline-item">
            <span className="label">Duration:</span>
            <span className="value">{formatDuration()}</span>
          </div>
        </div>
      </div>

      {/* Error Card (if failed) */}
      {job.error?.message && (
        <div className="detail-card error-card">
          <h2>Error Details</h2>
          <div className="error-info">
            <div className="error-message">
              <span className="label">Message:</span>
              <pre>{job.error.message}</pre>
            </div>
            {job.error.details && (
              <div className="error-details">
                <span className="label">Details:</span>
                <pre>{job.error.details}</pre>
              </div>
            )}
            <div className="error-timestamp">
              <span className="label">Time:</span>
              <span>{formatDate(job.error.timestamp)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Logs Card */}
      {job.logs && job.logs.length > 0 && (
        <div className="detail-card logs-card">
          <h2>Logs ({job.logs.length})</h2>
          <div className="logs-container">
            {job.logs.slice().reverse().map((log, idx) => (
              <div key={idx} className={`log-entry log-${log.level}`}>
                <span className="log-timestamp">{formatDate(log.timestamp)}</span>
                <span className="log-phase">[{log.phase}]</span>
                <span className="log-message">{log.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="detail-actions">
        {job.status === 'failed' && hasPermission('training.retry') && (
          <button className="action-button retry-button" onClick={handleRetry}>
            🔄 Retry Job
          </button>
        )}
        {(job.status === 'queued' || job.status === 'training' || job.status === 'preparing_data') &&
          hasPermission('training.cancel') && (
            <button className="action-button cancel-button" onClick={handleCancel}>
              ❌ Cancel Job
            </button>
          )}
      </div>
    </div>
  );
};

export default TrainingJobDetail;
